import {RETRY_INTERVAL, RETRY_LIMIT} from "@/constants/index";
import {log} from "@/utils/log";

interface AdGoogleWrapper {
  loadConfig: (tag: string) => Promise<unknown>;
  createSlot: (tag: string) => Promise<void>;
  displaySlot: (tag: string) => Promise<void>;
}

declare global {
  interface Window {
    ADGW?: AdGoogleWrapper;
  }
}

/**
 * Loads the ad configuration of a page, then creates and displays its slots, once the ADGW script of the ad provider is
 * there. That script is a third-party one: it arrives when it can, so it is waited for (retryLimit x retryInterval ms).
 *
 * Resolves true once the slots are displayed, false when the script never arrived: there is nothing to display then, and
 * the caller must not go on with the ad layout work (the code used to carry on and threw "ADGW is not defined").
 * A slot that fails is logged and does not fail the page; a configuration that cannot be loaded rejects.
 */
export async function loadAndDisplayAds(
  pageId: string,
  tags: string[],
  {retryLimit = RETRY_LIMIT, retryInterval = RETRY_INTERVAL} = {},
): Promise<boolean> {
  let adgw = window?.ADGW;
  for (let retry = 0; !adgw && retry < retryLimit; retry++) {
    await new Promise((resolve) => setTimeout(resolve, retryInterval));
    adgw = window?.ADGW;
  }
  if (!adgw) {
    // Mostly a visitor who blocks ads: for the console, not for the server
    log.warn(
      {event: "ads.script_unavailable", retries: retryLimit},
      `ADGW script didn't load after ${retryLimit} retries`,
    );
    return false;
  }

  await adgw.loadConfig(pageId);
  // Reassigned to a const so the narrowing above (adgw is defined) survives inside the closure below
  const resolvedAdgw = adgw;
  try {
    await Promise.all(
      tags.map(async (tag) => {
        await resolvedAdgw.createSlot(tag);
        await resolvedAdgw.displaySlot(tag);
      }),
    );
  } catch (error: unknown) {
    log.error({event: "ads.slot_failed", err: error}, "Create or display slot error");
  }
  return true;
}
